import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { common, financedEmissions } from "../../i18n";
import { Cat15Filter } from "../../models/dto/cat15/cat15.types";
import { SectorOption } from "../../models/dto/cat15/cat15.api";
import { getSectors } from "../../service/cat15.service";
import CustomDatePicker from "../input/CustomDatePicker";
import SectorMultiSelect from "./SectorMultiSelect";

/** All possible DQS levels shown in the DQS Level filter. */
const DQS_LEVELS = [1, 2, 3, 4, 5];

export interface FilterOption {
  value: string;
  label: string;
  /** greyed out and not selectable in the dropdown */
  disabled?: boolean;
}

interface FieldDef {
  key: keyof Cat15Filter;
  label: string;
  options: FilterOption[];
  /** render as a multi-select with chips (sector) */
  multi?: boolean;
}

interface Props {
  value: Cat15Filter;
  onChange: (next: Cat15Filter) => void;
  onReset?: () => void;
  /** opens the export dialog (download icon at the end of the bar) */
  onExport?: () => void;
}

const Cat15FilterBar = ({ value, onChange, onReset, onExport }: Props) => {
  const { t, i18n } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });

  // Level-1 ISIC sections for the sector filter (ISIC = primary taxonomy). Values are
  // section codes (e.g. "C") — what BE filters/groups on when taxonomy=isic.
  const [sectors, setSectors] = useState<SectorOption[]>([]);
  useEffect(() => {
    let active = true;
    getSectors("isic").then((s) => active && setSectors(s));
    return () => {
      active = false;
    };
  }, []);

  const isThai = i18n.language?.startsWith("th");
  const sectorOptions: FilterOption[] = sectors.map((s) => ({
    value: s.value,
    label: isThai ? s.labelTh : s.labelEn,
  }));

  const allOption: FilterOption = { value: "all", label: t("Filter-All") };

  const fields: FieldDef[] = [
    {
      key: "reportingYear",
      label: t("Filter-Reporting-Year"),
      options: [2025, 2024, 2023].map((y) => ({
        value: String(y),
        label: String(y),
      })),
    },
    {
      key: "assetClass",
      label: t("Filter-Asset-Class"),
      options: [
        allOption,
        { value: "business", label: t("Asset-Business-Loans") },
        { value: "listed", label: t("Asset-Listed-Equity") },
        { value: "project", label: t("Asset-Project-Finance") },
      ],
    },
    {
      key: "sector",
      label: t("Filter-Sector"),
      multi: true,
      options: sectorOptions,
    },
    {
      key: "listingStatus",
      label: t("Filter-Listing-Status"),
      options: [
        allOption,
        { value: "listed", label: t("Listed") },
        { value: "unlisted", label: t("Unlisted") },
      ],
    },
    {
      key: "dqsLevel",
      label: t("Filter-DQS-Level"),
      options: [
        allOption,
        ...DQS_LEVELS.map((d) => ({
          value: String(d),
          label: `DQS ${d}`,
        })),
      ],
    },
  ];

  const handleChange = (key: keyof Cat15Filter, raw: string) => {
    const parsed = key === "reportingYear" ? Number(raw) : raw;
    onChange({ ...value, [key]: parsed } as Cat15Filter);
  };

  const handleMultiChange = (key: keyof Cat15Filter, next: string[]) => {
    onChange({ ...value, [key]: next } as Cat15Filter);
  };

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      alignItems="flex-end"
      gap="16px"
      bgcolor="var(--white)"
      border="1px solid var(--gray5)"
      borderRadius="8px"
      p="16px 20px"
    >
      {/* Figma 4173:71: a fixed 200px Reporting Year, then four equal fields */}
      {fields.map((field) => (
        <Box
          key={field.key}
          flex={field.key === "reportingYear" ? "0 0 200px" : "1 1 0"}
          minWidth="150px"
        >
          {field.key === "reportingYear" ? (
            // Year picker (calendar icon + scrollable year view) to match the
            // Overview / Total Emissions "Year" filter.
            <CustomDatePicker
              label={field.label}
              info={field.label}
              showInfo={false}
              views={["year"]}
              format="YYYY"
              value={dayjs(`${value.reportingYear}-01-01`)}
              defaultDate={dayjs(`${value.reportingYear}-01-01`)}
              minDate={dayjs("2020-01-01")}
              handleOnChange={(d: Dayjs | null) =>
                d && onChange({ ...value, reportingYear: d.year() })
              }
            />
          ) : field.multi ? (
            <SectorMultiSelect
              label={field.label}
              options={field.options}
              value={value.sector}
              onChange={(next) => handleMultiChange("sector", next)}
            />
          ) : (
            <FormControl fullWidth size="small">
              <InputLabel id={`${field.key}-label`} shrink>
                {field.label}
              </InputLabel>
              <Select
                labelId={`${field.key}-label`}
                label={field.label}
                value={String(value[field.key])}
                onChange={(e) => handleChange(field.key, e.target.value)}
              >
                {field.options.map((opt) => (
                  <MenuItem
                    key={opt.value}
                    value={opt.value}
                    disabled={opt.disabled}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      ))}
      {onReset && (
        // Figma 4173:160: 24/7 padding, 16/24 medium green, 64px floor
        <Button
          variant="text"
          color="green"
          onClick={onReset}
          sx={{
            mb: "2px",
            minWidth: 64,
            px: "24px",
            py: "7px",
            fontSize: "16px",
            fontWeight: 500,
            lineHeight: "24px",
            textTransform: "none",
          }}
        >
          {t("Reset")}
        </Button>
      )}
      {onExport && (
        <Tooltip title={t("Export")}>
          <IconButton
            onClick={onExport}
            sx={{
              mb: "2px",
              color: "var(--white)",
              bgcolor: "var(--green)",
              borderRadius: "8px",
              "&:hover": { bgcolor: "var(--green-dark)" },
            }}
          >
            <FileDownloadIcon />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default Cat15FilterBar;
